/**
 * useMarkupDrag: Shared drag handler for shapes and handles
 * Feature: 049-critique-photo-markup
 *
 * Consolidates all drag logic (whole-shape dragging, handle dragging)
 * Used by both MarkupCanvas and drawing tools for consistent behavior
 */

import { useState, useRef, useCallback, useMemo } from 'react';
import type { MarkupShape } from '@/types/markup.types';
import { getShapeHandles, updateShapeFromHandle, translateShape } from '@/utils/markupTools';
import { hitTestShapes } from '@/utils/markupHitTest';
import { pixelToPercentage, transformShapeForViewBox, imageYToViewBoxY } from '@/utils/markupRenderer';
import logger from '@/utils/logger';

/**
 * Tolerance for handle click detection (percentage units)
 * Consistent with stroke hit testing tolerance in markupHitTest.ts
 */
const HANDLE_TOLERANCE = 2;

interface DragState {
  shapeNumber: number;
  startX: number;
  startY: number;
  originalShape: MarkupShape;
  handleType?: 'handle1' | 'handle2'; // Which handle is being dragged (undefined = whole shape)
}

interface UseMarkupDragOptions {
  shapes: MarkupShape[];
  selectedShapeId: number | null;  // Now uses shape.number for identification
  onShapeClick?: (shapeNumber: number) => void;
  onShapeMove?: (shapeNumber: number, shape: MarkupShape) => void;
  containerRef: React.RefObject<HTMLDivElement | SVGSVGElement>;
  photoAspectRatio: number;
  readOnly?: boolean;
}

/**
 * Get relative coordinates within container (percentage)
 */
function getRelativeCoords(
  e: React.MouseEvent,
  containerRef: React.RefObject<HTMLDivElement | SVGSVGElement>,
  photoAspectRatio: number
): { x: number; y: number } {
  if (!containerRef.current) return { x: 0, y: 0 };

  const rect = containerRef.current.getBoundingClientRect();
  const containerWidth = rect.width;
  const containerHeight = rect.height;
  const containerAspectRatio = containerWidth / containerHeight;

  // Calculate actual photo dimensions within container (object-contain behavior)
  let photoWidth, photoHeight, offsetX, offsetY;

  if (photoAspectRatio > containerAspectRatio) {
    // Photo is wider - letterboxing on top/bottom
    photoWidth = containerWidth;
    photoHeight = containerWidth / photoAspectRatio;
    offsetX = 0;
    offsetY = (containerHeight - photoHeight) / 2;
  } else {
    // Photo is taller - pillarboxing on left/right
    photoHeight = containerHeight;
    photoWidth = containerHeight * photoAspectRatio;
    offsetX = (containerWidth - photoWidth) / 2;
    offsetY = 0;
  }

  // Convert mouse position to photo-relative coordinates
  const pixelX = e.clientX - rect.left - offsetX;
  const pixelY = e.clientY - rect.top - offsetY;

  // Convert to percentage (0-100 for X, 0-viewBoxHeight for Y)
  return pixelToPercentage(pixelX, pixelY, photoWidth, photoHeight, photoAspectRatio);
}

export function useMarkupDrag({
  shapes,
  selectedShapeId,
  onShapeClick,
  onShapeMove,
  containerRef,
  photoAspectRatio,
  readOnly = false,
}: UseMarkupDragOptions) {
  const viewBoxHeight = 100 / photoAspectRatio;
  const [dragState, setDragState] = useState<DragState | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const [hoveredTarget, setHoveredTarget] = useState<{ type: 'handle' | 'shape' | 'none' } | null>(null);

  /**
   * Check if click is on a handle, shape, or empty space
   * Returns: { type: 'handle' | 'shape' | 'none', shapeNumber?, handleId? }
   *
   * BUG FIX (#480): Transform both shape and click coordinates to viewBox space
   * to match rendered handle positions
   */
  const detectClickTarget = useCallback(
    (x: number, y: number) => {
      // First check handles of selected shape (if any)
      if (selectedShapeId !== null) {
        const selectedShape = shapes.find((s) => s.number === selectedShapeId);
        if (selectedShape) {
          // Transform shape to viewBox space to match rendered handle positions
          const transformedShape = transformShapeForViewBox(selectedShape, photoAspectRatio);
          const handles = getShapeHandles(transformedShape);

          // Transform click coordinates from image space to viewBox space for comparison
          const clickYInViewBox = imageYToViewBoxY(y, photoAspectRatio);

          for (const handle of handles) {
            const distance = Math.sqrt(
              Math.pow(x - handle.x, 2) + Math.pow(clickYInViewBox - handle.y, 2)
            );
            if (distance <= HANDLE_TOLERANCE) {
              return { type: 'handle' as const, shapeNumber: selectedShapeId, handleId: handle.id };
            }
          }
        }
      }

      // Check if clicking on any shape (returns shape.number)
      const hitShapeNumber = hitTestShapes(x, y, shapes, viewBoxHeight);
      if (hitShapeNumber !== null) {
        return { type: 'shape' as const, shapeNumber: hitShapeNumber };
      }

      // Clicking on empty space
      return { type: 'none' as const };
    },
    [shapes, selectedShapeId, viewBoxHeight, photoAspectRatio]
  );

  /**
   * Handle mouse down - start dragging shape or handle if clicked
   * Returns true if drag started (clicked on shape/handle), false otherwise
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent): boolean => {
      if (readOnly) return false;

      const coords = getRelativeCoords(e, containerRef, photoAspectRatio);
      const target = detectClickTarget(coords.x, coords.y);

      // Click on empty space - allow tool to handle it
      if (target.type === 'none') {
        return false;
      }

      // Click on shape - select it and start potential drag
      if (target.type === 'shape' && target.shapeNumber !== undefined) {
        const shape = shapes.find((s) => s.number === target.shapeNumber);
        if (!shape) return false;

        logger.debug('useMarkupDrag', 'Starting shape drag:', target.shapeNumber);
        onShapeClick?.(target.shapeNumber);

        const newDragState: DragState = {
          shapeNumber: target.shapeNumber,
          startX: coords.x,
          startY: coords.y,
          originalShape: shape,
        };
        setDragState(newDragState);
        dragStateRef.current = newDragState;
        return true;
      }

      // Click on handle - start handle drag
      if (target.type === 'handle' && target.handleId && target.shapeNumber !== undefined) {
        const shape = shapes.find((s) => s.number === target.shapeNumber);
        if (!shape) return false;

        logger.debug('useMarkupDrag', 'Starting handle drag:', target.shapeNumber, target.handleId);

        const newDragState: DragState = {
          shapeNumber: target.shapeNumber,
          startX: coords.x,
          startY: coords.y,
          originalShape: shape,
          handleType: target.handleId,
        };
        setDragState(newDragState);
        dragStateRef.current = newDragState;
        return true;
      }

      return false;
    },
    [shapes, selectedShapeId, readOnly, containerRef, photoAspectRatio, onShapeClick, detectClickTarget]
  );

  /**
   * Handle mouse move - update shape or handle position, and track hover state
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const coords = getRelativeCoords(e, containerRef, photoAspectRatio);
      const currentDragState = dragStateRef.current;

      // Update hover state (only when not dragging)
      if (!currentDragState && !readOnly) {
        const target = detectClickTarget(coords.x, coords.y);
        setHoveredTarget(target);
      }

      // Handle dragging
      if (!currentDragState || !onShapeMove) return;

      if (currentDragState.handleType) {
        // Dragging a handle - update shape geometry
        const updatedShape = updateShapeFromHandle(
          currentDragState.originalShape,
          currentDragState.handleType,
          coords.x,
          coords.y,
          viewBoxHeight
        );
        onShapeMove(currentDragState.shapeNumber, updatedShape);
      } else {
        // Dragging whole shape - translate it
        const deltaX = coords.x - currentDragState.startX;
        const deltaY = coords.y - currentDragState.startY;

        const updatedShape = translateShape(
          currentDragState.originalShape,
          deltaX,
          deltaY,
          viewBoxHeight
        );
        onShapeMove(currentDragState.shapeNumber, updatedShape);
      }
    },
    [onShapeMove, containerRef, photoAspectRatio, viewBoxHeight, readOnly, detectClickTarget]
  );

  /**
   * Handle mouse up - complete drag
   */
  const handleMouseUp = useCallback(() => {
    if (dragStateRef.current) {
      logger.debug('useMarkupDrag', 'Completed drag:', dragStateRef.current.shapeNumber);
      setDragState(null);
      dragStateRef.current = null;
    }
  }, []);

  /**
   * Handle mouse leave - clear hover state
   */
  const handleMouseLeave = useCallback(() => {
    setHoveredTarget(null);
  }, []);

  /**
   * Compute cursor based on drag and hover state
   * Returns null to let caller provide appropriate default (crosshair for tools, default for canvas)
   */
  const cursor = useMemo(() => {
    if (dragState !== null) return 'grabbing';
    if (hoveredTarget?.type === 'shape' || hoveredTarget?.type === 'handle') return 'grab';
    return null; // Let caller decide default
  }, [dragState, hoveredTarget]);

  return {
    isDragging: dragState !== null,
    dragState,
    hoveredTarget,
    cursor,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    detectClickTarget,
  };
}
